import { Button, DatePicker, Drawer, Select, Space, Tag, Input, Dropdown } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IoFunnelOutline } from "react-icons/io5";
import { MdSave, MdDelete } from "react-icons/md";
import { BsBookmark, BsBookmarkFill } from "react-icons/bs";
import { loadSavedFilters, saveFilter, deleteSavedFilter, setCurrentFilter } from "../../../redux/rtk/features/filter/filterSlice";
import FilterBuilder from "./FilterBuilder";
import SaveFilterDialog from "./SaveFilterDialog";
import FilterPresets from "./FilterPresets";

const { RangePicker } = DatePicker;

export default function AdvancedFilter({
  page,
  filters = [],
  onFilterChange,
  pageConfig,
  setPageConfig,
  onOpen, // New prop to notify when filter is opened
  mode = "both" // New prop: "both", "filterOnly", "savedOnly"
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filterState = useSelector((state) => state.filter) || {};
  const savedFilters = Array.isArray(filterState.savedFilters) ? filterState.savedFilters : [];
  const loading = !!filterState.loading;

  const [open, setOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [compositeFilters, setCompositeFilters] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // تحميل الفلاتر عند فتح Drawer أو Dropdown
  useEffect(() => {
    if (page && (open || dropdownOpen)) {
      dispatch(loadSavedFilters(page));
    }
  }, [open, dropdownOpen, page, dispatch]);

  useEffect(() => {
    // تطبيق الفلاتر على pageConfig
    if (compositeFilters && compositeFilters.length > 0) {
      setPageConfig((prev) => ({
        ...prev,
        compositeFilters: JSON.stringify(compositeFilters),
        page: 1,
      }));
    } else {
      setPageConfig((prev) => {
        const newConfig = { ...prev, page: 1 };
        delete newConfig.compositeFilters;
        return newConfig;
      });
    }

    if (onFilterChange) {
      onFilterChange(activeFilters, compositeFilters);
    }
  }, [activeFilters, compositeFilters, onFilterChange, setPageConfig]);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => {
      const filtered = prev.filter((f) => f.key !== key);
      if (value !== null && value !== undefined && value !== "") {
        return [...filtered, { key, value }];
      }
      return filtered;
    });

    // تحديث pageConfig
    setPageConfig((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleDateRangeChange = (key, dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      if (key === "dateRange") {
        handleFilterChange("startDate", startDate);
        handleFilterChange("endDate", endDate);
      } else {
        handleFilterChange(`${key}_startDate`, startDate);
        handleFilterChange(`${key}_endDate`, endDate);
      }
    } else {
      if (key === "dateRange") {
        handleFilterChange("startDate", null);
        handleFilterChange("endDate", null);
      } else {
        handleFilterChange(`${key}_startDate`, null);
        handleFilterChange(`${key}_endDate`, null);
      }
    }
  };

  const handleRemoveFilter = (key) => {
    setActiveFilters((prev) => prev.filter((f) => f.key !== key));
    setPageConfig((prev) => {
      const newConfig = { ...prev };
      delete newConfig[key];
      return { ...newConfig, page: 1 };
    });
  };

  const handleApplyPreset = (preset) => {
    setSelectedPreset(preset);
    const newFilters = [];

    if (preset.filters) {
      preset.filters.forEach((filter) => {
        if (filter.field && filter.value !== undefined) {
          newFilters.push({
            key: filter.field,
            value: filter.value,
          });
        }
      });
    }

    setActiveFilters(newFilters);

    // تطبيق الفلتر على pageConfig
    const newConfig = { ...pageConfig, page: 1 };
    newFilters.forEach((f) => {
      newConfig[f.key] = f.value;
    });
    setPageConfig(newConfig);
  };

  const handleSaveFilter = async (name, isDefault) => {
    const filterData = {
      name,
      page,
      filters: [...activeFilters, ...compositeFilters],
      isDefault,
    };

    await dispatch(saveFilter(filterData));
    setSaveDialogOpen(false);
    if (page) {
      dispatch(loadSavedFilters(page));
    }
  };

  const handleLoadSavedFilter = (savedFilter) => {
    if (savedFilter.filters && Array.isArray(savedFilter.filters)) {
      // فصل الفلاتر البسيطة عن المركبة
      const simpleFilters = [];
      const composite = [];

      savedFilter.filters.forEach((f) => {
        if (f.field && f.operator) {
          // فلتر مركب
          composite.push({
            field: f.field,
            operator: f.operator,
            value: f.value,
            logic: f.logic || 'and',
          });
        } else {
          // فلتر بسيط
          simpleFilters.push({
            key: f.key || f.field,
            value: f.value,
          });
        }
      });

      setActiveFilters(simpleFilters);
      setCompositeFilters(composite);

      // تطبيق الفلاتر على pageConfig
      // نبدأ بتهيئة Config نظيفة لعدم دمج الفلاتر السابقة
      const newConfig = {
        page: 1,
        count: pageConfig.count || 10
      };

      // الاحتفاظ ببعض القيم الثابتة إذا كانت موجودة
      if (pageConfig.isAgent) newConfig.isAgent = pageConfig.isAgent;
      if (pageConfig.query) newConfig.query = pageConfig.query;

      // تطبيق الفلاتر البسيطة
      simpleFilters.forEach((f) => {
        newConfig[f.key] = f.value;
      });

      // تطبيق الفلاتر المركبة
      if (composite.length > 0) {
        newConfig.compositeFilters = JSON.stringify(composite);
      } else {
        delete newConfig.compositeFilters;
      }

      setPageConfig(newConfig);
      dispatch(setCurrentFilter(savedFilter));

      // إغلاق Dropdown بعد التطبيق
      setDropdownOpen(false);
    }
  };

  const handleDeleteSavedFilter = async (id) => {
    await dispatch(deleteSavedFilter(id));
    if (page) {
      dispatch(loadSavedFilters(page));
    }
  };

  const handleClearAll = () => {
    setActiveFilters([]);
    setCompositeFilters([]);
    setSelectedPreset(null);
    setPageConfig({ page: 1, count: pageConfig.count || 10 });
  };

  const getFilterValue = (key) => {
    const filter = activeFilters.find((f) => f.key === key);
    return filter ? filter.value : undefined;
  };

  // قائمة الفلاتر المحفوظة للـ Dropdown
  const getSavedFiltersMenu = () => {
    const items = savedFilters.length > 0 ? savedFilters.map((filter) => ({
      key: String(filter.id),
      label: (
        <div
          className="flex items-center justify-between gap-4 py-1"
          onClick={(e) => e.stopPropagation()}>
          <div
            className="flex items-center gap-2 flex-1 cursor-pointer hover:text-blue-500 transition-colors"
            onClick={() => {
              handleLoadSavedFilter(filter);
              setDropdownOpen(false);
            }}>
            {filter.isDefault ? (
              <BsBookmarkFill className="text-blue-500 text-sm" />
            ) : (
              <BsBookmark className="text-gray-400 text-sm" />
            )}
            <span className="text-sm font-medium">
              {filter.name} {typeof filter.count === 'number' ? `(${filter.count})` : ''}
            </span>
            {!!filter.isDefault && (
              <Tag color="blue" size="small" className="m-0 text-[10px] py-0 px-1">{t("common.default")}</Tag>
            )}
          </div>
          <Space size="small">
            <Button
              type="text"
              size="small"
              danger
              icon={<MdDelete />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSavedFilter(filter.id);
              }}
              className="flex items-center justify-center w-7 h-7"
            />
          </Space>
        </div>
      ),
    })) : [
      {
        key: 'no-filters',
        label: (
          <div className="py-2 px-4 text-gray-500 text-sm text-center min-w-[200px]">
            {t("common.noSavedFilters") || "لا توجد فلاتر محفوظة"}
          </div>
        ),
        disabled: true,
      }
    ];

    return { items };
  };

  const handleOpenDrawer = () => {
    setOpen(true);
    if (onOpen) onOpen();
  };

  return (
    <>
      <Space>
        {/* زر الفلترة المتقدمة */}
        {(mode === "both" || mode === "filterOnly") && (
          <Button
            type="default"
            icon={<IoFunnelOutline />}
            onClick={handleOpenDrawer}
            className="flex items-center gap-2">
            {t("common.filter")}
            {activeFilters.length > 0 && (
              <Tag color="blue" className="m-0">{activeFilters.length}</Tag>
            )}
          </Button>
        )}

        {/* Dropdown للفلاتر المحفوظة */}
        {(mode === "both" || mode === "savedOnly") && (
          <Dropdown
            menu={getSavedFiltersMenu()}
            trigger={['click']}
            placement="bottomLeft"
            open={dropdownOpen}
            onOpenChange={(isOpen) => {
              setDropdownOpen(isOpen);
              if (isOpen) {
                if (onOpen) onOpen();
                if (page) dispatch(loadSavedFilters(page));
              }
            }}
            overlayClassName="saved-filters-dropdown">
            <Button
              type="default"
              icon={<BsBookmarkFill />}
              className="flex items-center gap-2"
              loading={loading}>
              {t("common.savedFilters")}
              {savedFilters.length > 0 && (
                <Tag color="blue" className="m-0">{savedFilters.length}</Tag>
              )}
            </Button>
          </Dropdown>
        )}

        {/* Clear All button */}
        {(activeFilters.length > 0 || compositeFilters.length > 0) && (
          <Button
            type="link"
            danger
            onClick={handleClearAll}
            className="flex items-center p-0 font-medium">
            {t("common.clearAll") || "إلغاء الكل"}
          </Button>
        )}
      </Space>

      <Drawer
        title={
          <div className="flex items-center justify-between">
            <span>{t("common.advancedFilter")}</span>
            <Space>
              <Button
                size="small"
                icon={<MdSave />}
                onClick={() => setSaveDialogOpen(true)}
                disabled={activeFilters.length === 0 && compositeFilters.length === 0}>
                {t("common.saveFilter")}
              </Button>
              <Button size="small" danger onClick={handleClearAll}>
                {t("common.clearAll")}
              </Button>
            </Space>
          </div>
        }
        open={open}
        onClose={() => setOpen(false)}
        width={700}
        placement="right">
        <div className="space-y-4 px-4 pb-4">
          {/* الفلاتر المحفوظة */}
          {savedFilters && savedFilters.length > 0 && (
            <div>
              <h3 className="mb-3 font-semibold text-base">{t("common.savedFilters")}</h3>
              <div className="space-y-2">
                {savedFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div
                      className="flex items-center gap-2 flex-1 cursor-pointer"
                      onClick={() => handleLoadSavedFilter(filter)}>
                      {filter.isDefault ? (
                        <BsBookmarkFill className="text-blue-500" />
                      ) : (
                        <BsBookmark className="text-gray-400" />
                      )}
                      <span className="font-medium">
                        {filter.name} {typeof filter.count === 'number' ? `(${filter.count})` : ''}
                      </span>
                      {!!filter.isDefault && (
                        <Tag color="blue" size="small">{t("common.default")}</Tag>
                      )}
                    </div>
                    <Space size="small">
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleLoadSavedFilter(filter)}
                        className="text-blue-500 hover:text-blue-700">
                        {t("common.apply")}
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<MdDelete />}
                        onClick={() => handleDeleteSavedFilter(filter.id)}
                        className="hover:bg-red-50">
                        {t("common.delete")}
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* الفلاتر الافتراضية */}
          <FilterPresets
            page={page}
            onApplyPreset={handleApplyPreset}
            selectedPreset={selectedPreset}
          />

          {/* الفلاتر البسيطة */}
          <div>
            <h3 className="mb-3 font-semibold text-base">{t("common.filters")}</h3>
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <Space direction="vertical" className="w-full" size="middle">
                {filters.map((filter) => {
                  if (filter.type === "dateRange") {
                    const startDate = filter.key === "dateRange" ? getFilterValue("startDate") : getFilterValue(`${filter.key}_startDate`);
                    const endDate = filter.key === "dateRange" ? getFilterValue("endDate") : getFilterValue(`${filter.key}_endDate`);
                    return (
                      <div key={filter.key}>
                        <label className="block mb-1 text-sm font-medium">
                          {filter.label}
                        </label>
                        <RangePicker
                          className="w-full"
                          value={
                            startDate && endDate
                              ? [dayjs(startDate), dayjs(endDate)]
                              : null
                          }
                          onChange={(dates) => handleDateRangeChange(filter.key, dates)}
                          format="YYYY-MM-DD"
                        />
                      </div>
                    );
                  } else if (filter.type === "select") {
                    return (
                      <div key={filter.key}>
                        <label className="block mb-1 text-sm font-medium">
                          {filter.label}
                        </label>
                        <Select
                          className="w-full"
                          placeholder={filter.label}
                          mode={filter.mode || "single"}
                          value={getFilterValue(filter.key)}
                          onChange={(value) => handleFilterChange(filter.key, value)}
                          options={filter.options}
                          allowClear
                          showSearch
                          loading={filter.loading}
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </div>
                    );
                  } else if (filter.type === "text") {
                    return (
                      <div key={filter.key}>
                        <label className="block mb-1 text-sm font-medium">
                          {filter.label}
                        </label>
                        <Input
                          className="w-full"
                          placeholder={filter.label}
                          value={getFilterValue(filter.key) || ""}
                          onChange={(e) => handleFilterChange(filter.key, e.target.value || null)}
                          allowClear
                        />
                      </div>
                    );
                  }
                  return null;
                })}
              </Space>
            </div>
          </div>

          {/* الفلتر المركب */}
          <div>
            <h3 className="mb-2 font-semibold">{t("common.compositeFilter")}</h3>
            <FilterBuilder
              filters={compositeFilters}
              onChange={setCompositeFilters}
              availableFields={filters}
            />
          </div>

          {/* الفلاتر النشطة */}
          {(activeFilters.length > 0 || compositeFilters.length > 0) && (
            <div>
              <h3 className="mb-2 font-semibold">{t("common.activeFilters")}</h3>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => {
                  const filterConfig = filters.find((f) => f.key === filter.key);
                  const displayValue = filterConfig?.options?.find(
                    (opt) => opt.value === filter.value
                  )?.label || filter.value;
                  return (
                    <Tag
                      key={filter.key}
                      closable
                      onClose={() => handleRemoveFilter(filter.key)}
                      color="blue">
                      {filterConfig?.label || filter.key}: {displayValue}
                    </Tag>
                  );
                })}
                {compositeFilters.map((filter, index) => (
                  <Tag
                    key={`composite-${index}`}
                    closable
                    onClose={() => {
                      setCompositeFilters((prev) =>
                        prev.filter((_, i) => i !== index)
                      );
                    }}
                    color="purple">
                    {filter.field} {filter.operator} {filter.value}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      </Drawer>

      <SaveFilterDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveFilter}
        filterCount={activeFilters.length + compositeFilters.length}
      />
    </>
  );
}
